// Whole-collection client for the promoted `players` + `elo_adjustments`
// entities (Stage 2). On the http backend these hit the dedicated /players and
// /elo-adjustments endpoints (real Postgres tables); on memory/tests they fall
// back to the key/value Storage the app used before. The app keeps its
// load-all / save-all model — see docs/architecture/SCHEMA.md.
//
// IMPORTANT: EVERY writer of the player list must go through persistPlayers
// (not Storage directly), or players split between the table and the /kv blob.
import { Storage } from '../storage';
import { apiFetch } from './http';
import { SaveRefused } from '../saveWithRetry';

/**
 * A FAILED READ IS NOT AN EMPTY DATABASE (#272).
 *
 * This returned `[]` on any non-2xx, and `apiFetch` does not throw on one — so
 * a 401 or a 502 came back looking exactly like a club with no players. The
 * mount loader read that as «not migrated yet», seeded the 200 demo players,
 * and wrote them through the whole-collection PUT, whose `deleteMany` removed
 * every real player on the platform. One failed request at boot, no attacker
 * required, and the `try/catch` around it never fired because nothing threw.
 *
 * So it throws. A caller that wants to carry on without players must say so by
 * catching, which is a decision someone can read, rather than inheriting one
 * from a silent empty array.
 */
/**
 * THE CLUB'S PLAYERS, NOT THE PLATFORM'S (#1060).
 *
 * `GET /members/players` answers this organization's roster plus the players
 * in its own events, with the organization taken from the resolved scope. The
 * platform-wide `GET /players` is platform staff's now (/sa's rating
 * correction reads it); a club screen asking for it is refused.
 *
 * Of a player who is not the club's to maintain — another club's member, or a
 * registered person on no roster — the answer carries name and rating only,
 * never an address or a telephone number.
 */
export async function fetchPlayers(): Promise<any[]> {
  if (Storage.backend === 'http') {
    const res = await apiFetch('/members/players');
    if (!res.ok) throw new Error(`[players] GET failed: ${res.status}`);
    return (await res.json()) as any[];
  }
  const r = await Storage.get('players_v1');
  return r?.value ? JSON.parse(r.value) : [];
}

/**
 * The whole-collection write, reached on the http backend where it is CLOSED.
 *
 * Its own class because callers of the blob path swallow write errors by
 * design; swallowing this one would make the closed door look like a save.
 */
export class WholeCollectionWriteClosed extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WholeCollectionWriteClosed';
  }
}

export async function persistPlayers(list: any[]): Promise<void> {
  if (Storage.backend === 'http') {
    // CLOSED (#272, slice 3). This upserted the GLOBAL player list from a copy
    // the browser read earlier and deleted every row not in it — with no tenant
    // column, so it deleted across every club. Every writer now names what it
    // changes: createPlayer, updatePlayerProfile, deletePlayerCommand and
    // adjustPlayerElo; ratings otherwise move only through recorded results.
    throw new WholeCollectionWriteClosed(
      '[players] Whole-collection writes are closed (#272). Use a command: ' +
        'createPlayer, updatePlayerProfile, deletePlayerCommand, adjustPlayerElo.',
    );
  }
  await Storage.set('players_v1', JSON.stringify(list));
}

export async function fetchEloAdjustments(): Promise<any[]> {
  if (Storage.backend === 'http') {
    // The corrections of the players this club sees (#1060), not the
    // platform's whole log — see `fetchPlayers`.
    const res = await apiFetch('/members/players/elo-adjustments');
    if (!res.ok) return [];
    return (await res.json()) as any[];
  }
  const r = await Storage.get('elo_adjustments_v1');
  return r?.value ? JSON.parse(r.value) : [];
}

export async function persistEloAdjustments(list: any[]): Promise<void> {
  if (Storage.backend === 'http') {
    // CLOSED for the same reason, and with a sharper edge: this is an AUDIT
    // log. Rewriting it wholesale from a browser's copy is how a record of a
    // rating change disappears. `adjustPlayerElo` appends one entry inside the
    // same transaction as the rating it explains.
    throw new WholeCollectionWriteClosed(
      '[elo-adjustments] Whole-collection writes are closed (#272). ' +
        'A manual correction and its audit entry are one command: adjustPlayerElo.',
    );
  }
  await Storage.set('elo_adjustments_v1', JSON.stringify(list));
}

// ── ARCHIVE COMMITS NO RATINGS (#795) ────────────────────────────────
//
// `commitPlayerRatings` and its helpers sent the deltas a browser measured at
// archive to POST /players/rating-commit. Retired with the route (owner
// decision 2026-10-31, option 1A): ratings move only through recorded results,
// which the server computes and claims per match.

/** True when ratings are the server's — results commit them, archive does not. */
export const ratingCommitsAreServerAuthoritative = () => Storage.backend === 'http';

// ── PLAYER CRUD IS A COMMAND (#272, slice 2) ─────────────────────────
//
// Every one of these used to rewrite the ENTIRE global player list to change
// one player — a list with no tenant column, so the save deleted or overwrote
// players another club had created since this tab loaded.

/** True when player CRUD goes through the server commands. */
export const playerCommandsAreServerAuthoritative = () => Storage.backend === 'http';

/**
 * THE DEMO BANK AS A FALLBACK — only where it is the club's own data (#1060).
 *
 * Several screens fall back to the built-in 200-player bank when the player
 * list is EMPTY. On the blob backend that bank is the club's local database.
 * On the API it is not: seed ids 1..200 are real rows in the platform table,
 * so a new club with no roster and no events would be shown 200 platform
 * players — and could commit ratings to them. There, an empty list stays empty.
 */
export function seedFallbackPlayers<T>(bank: T[]): T[] {
  return playerCommandsAreServerAuthoritative() ? [] : bank;
}

export type PlayerLookupResult =
  | { readonly state: 'ok'; readonly players: any[] }
  | { readonly state: 'refused'; readonly reason: string }
  | { readonly state: 'error'; readonly status: number | null };

/**
 * Find a player outside the club's world by their EXACT full name (#1060).
 *
 * The narrowed list no longer holds a guest from another club; this is how a
 * club reaches one to add to an event. Name and rating only come back.
 */
export async function lookupPlayersByName(name: string): Promise<PlayerLookupResult> {
  try {
    const res = await apiFetch(`/members/players/lookup?name=${encodeURIComponent(name.trim())}`);
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.ok) {
      return { state: 'ok', players: Array.isArray(body?.players) ? (body!.players as any[]) : [] };
    }
    if (res.status < 500 && typeof body?.error === 'string') {
      return { state: 'refused', reason: body.error };
    }
    return { state: 'error', status: res.status };
  } catch {
    return { state: 'error', status: null };
  }
}

const PLAYER_MESSAGES: Record<string, string> = {
  already_exists: 'En spiller med denne ID-en finnes allerede.',
  not_found: 'Spilleren finnes ikke lenger.',
  system_player: 'Systemspillere kan ikke slettes.',
  no_change: 'Ingen endring å lagre.',
  // GENERIC AGAIN (#197). It said «Du har ikke tilgang til å slette spillere»,
  // which was true while DELETE was the only route here that could answer 403.
  // Creating, editing and committing ratings can now too — the global block
  // has a write bar at last — so a message naming deletion would be wrong for
  // three of the five commands that share this map.
  forbidden: 'Du har ikke tilgang til dette.',
  // THE ONE REFUSAL THAT NEEDS EXPLAINING (#197). «Kunne ikke lagre» sends a
  // club administrator looking for a bug; the real answer is that this player
  // is another club's member, and their details are that club's to maintain.
  player_not_in_this_organization:
    'Denne spilleren er medlem i en annen klubb. Det er den klubben som vedlikeholder opplysningene.',
};

async function playerJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    if (res.status >= 500) throw new Error(`[players] ${res.status}`); // retryable
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    const code = body?.error ?? null;
    throw new SaveRefused((code && PLAYER_MESSAGES[code]) || fallback, res.status, code);
  }
  return (await res.json()) as T;
}

/** Create ONE player. An id that already exists is refused, not replaced. */
export async function createPlayer(player: any): Promise<any> {
  const res = await apiFetch('/players', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ player }),
  });
  const out = await playerJson<{ player: any }>(res, 'Kunne ikke lagre spilleren.');
  return out.player;
}

/**
 * Edit ONE player's profile.
 *
 * The server applies an allowlist, so the rating, the match count and the
 * system flag are unreachable from here however much this sends.
 */
export async function updatePlayerProfile(id: string, updates: any): Promise<any> {
  const res = await apiFetch(`/players/${encodeURIComponent(String(id))}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  const out = await playerJson<{ player: any }>(res, 'Kunne ikke lagre spilleren.');
  return out.player;
}

/** Delete ONE player. The server refuses a system player. */
export async function deletePlayerCommand(id: string): Promise<void> {
  const res = await apiFetch(`/players/${encodeURIComponent(String(id))}`, { method: 'DELETE' });
  await playerJson<{ deleted: number }>(res, 'Kunne ikke slette spilleren.');
}

/**
 * A manual rating correction AND its audit entry, in one transaction.
 *
 * The browser wrote them separately — the log through its own whole-list PUT
 * whose failure was caught and ignored — so a rating could move with the only
 * record of why it moved silently lost.
 */
export async function adjustPlayerElo(
  id: string,
  newElo: number,
  reason: string | null,
): Promise<{ player: any; adjustment: any }> {
  const res = await apiFetch(`/players/${encodeURIComponent(String(id))}/elo-adjustment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newElo, reason }),
  });
  return playerJson<{ player: any; adjustment: any }>(res, 'Kunne ikke lagre ELO-justeringen.');
}
